import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Building2, Plus, Trash2, Loader2, RefreshCw, UserPlus, Save } from 'lucide-react';
import { Sidebar } from '../../components/Sidebar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { adminApi, errMsg, dateTime } from '../brand/tracker/trackerApi';

const FIELD = 'w-full h-9 rounded-md px-3 text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] focus:outline-none focus:border-[#FF451A]/60';
const LABEL = 'text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white';
const BTN = 'rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors';
const PRIMARY = `${BTN} bg-[#FF451A] hover:bg-[#E63E17] text-white`;
const GHOST = `${BTN} border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white hover:border-[#FF451A]/40`;

const CompetitorRows = ({ rows, setRows, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {rows.map((r, i) => (
      <div key={i} className="flex gap-2">
        <input value={r.name} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Display name (e.g. Minimalist)" className={FIELD} data-testid={`${testid}-name-${i}`} />
        <input value={r.instagram_handle} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, instagram_handle: e.target.value } : x)))} placeholder="@instagram_handle" className={FIELD} data-testid={`${testid}-handle-${i}`} />
        <input value={r.hashtag || ''} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, hashtag: e.target.value } : x)))} placeholder="Custom #hashtag (optional — auto-detected)" className={FIELD} data-testid={`${testid}-hashtag-${i}`} />
        <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="rounded-md px-2 text-[#6B6B6B] hover:text-red-500" data-testid={`${testid}-remove-${i}`}><Trash2 size={14} /></button>
      </div>
    ))}
    <button type="button" onClick={() => setRows([...rows, { name: '', instagram_handle: '' }])} className={GHOST} data-testid={`${testid}-add`}><Plus size={12} /> Add competitor</button>
  </div>
);
const clean = (rows) => rows.filter((r) => r.name.trim() && r.instagram_handle.trim());

const CreateDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState('');
  const [rows, setRows] = useState([{ name: '', instagram_handle: '' }]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const a = await adminApi.post('/accounts', { brand_name: name, competitors: clean(rows) });
      toast.success(`${a.brand_name} brand account created`);
      setName(''); setRows([{ name: '', instagram_handle: '' }]);
      onCreated(a);
    } catch (err) { toast.error(errMsg(err, 'Could not create account')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="brand-account-create-dialog">
        <DialogHeader><DialogTitle>New brand account</DialogTitle><DialogDescription>Set up competitor tracking for a brand.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><label className={LABEL}>Brand name *</label><input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Foxtale" className={FIELD} data-testid="brand-account-name-input" /></div>
          <div><label className={LABEL}>Competitors</label><CompetitorRows rows={rows} setRows={setRows} testid="brand-account-create-comp" /></div>
          <div className="flex justify-end"><button type="submit" disabled={busy || !name.trim()} className={PRIMARY} data-testid="brand-account-create-submit">{busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create account</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const UserForm = ({ accountId, onCreated }) => {
  const [f, setF] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await adminApi.post(`/accounts/${accountId}/users`, f);
      toast.success(`Brand user ${u.email} created`);
      setF({ name: '', email: '', password: '' });
      onCreated();
    } catch (err) { toast.error(errMsg(err, 'Could not create user')); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <form onSubmit={submit} className="grid grid-cols-3 gap-2" data-testid="brand-user-form">
      <input required value={f.name} onChange={set('name')} placeholder="Name" className={FIELD} data-testid="brand-user-name" />
      <input required type="email" value={f.email} onChange={set('email')} placeholder="Email" className={FIELD} data-testid="brand-user-email" />
      <input required type="password" minLength={6} value={f.password} onChange={set('password')} placeholder="Password (6+ chars)" className={FIELD} data-testid="brand-user-password" />
      <div className="col-span-3 flex justify-end"><button type="submit" disabled={busy} className={PRIMARY} data-testid="brand-user-submit">{busy ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Create brand user</button></div>
    </form>
  );
};

const DetailSheet = ({ id, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => { if (id) adminApi.get(`/accounts/${id}`).then((x) => { setD(x); setRows(x.competitors.map(({ name, instagram_handle, hashtag }) => ({ name, instagram_handle, hashtag: hashtag || '' }))); }); }, [id]);
  useEffect(() => { setD(null); load(); }, [load]);
  const saveComps = async () => {
    setBusy('save');
    try { await adminApi.put(`/accounts/${id}/competitors`, { competitors: clean(rows) }); toast.success('Competitors saved'); load(); onChanged(); } catch (e) { toast.error(errMsg(e, 'Could not save')); } finally { setBusy(null); }
  };
  const scrape = async (force) => {
    setBusy('scrape');
    try {
      const r = await adminApi.scrape(id, force);
      if (r.status === 'cached') toast.info('Data is less than 24h old — showing cached results', { description: 'Use "Force refresh" to scrape again (uses Apify credits).' });
      else if (r.status === 'timeout') toast.warning('Scrape is taking longer than expected', { description: 'Try again in a few minutes.' });
      else toast.success(`Scrape done · ${r.posts_saved} creator posts · ${r.apify_runs} Apify runs`, { description: Object.entries(r.per_competitor || {}).map(([h, s]) => `@${h}: ${s.creator_posts}${s.errors?.length ? ' (error)' : ''}`).join(' · ') || 'No posts found' });
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e, 'Scrape failed')); } finally { setBusy(null); }
  };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="brand-account-detail">
        {!d ? <Loader2 className="animate-spin mt-10" size={16} /> : (
          <div className="space-y-6">
            <SheetHeader><SheetTitle data-testid="brand-account-detail-name">{d.brand_name}</SheetTitle><SheetDescription>{d.post_count} competitor posts · last updated {dateTime(d.last_scraped_at)}</SheetDescription></SheetHeader>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => scrape(false)} disabled={!!busy} className={GHOST} data-testid="brand-account-scrape-btn">{busy === 'scrape' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {busy === 'scrape' ? 'Scraping Instagram… (up to 4 min)' : 'Refresh data'}</button>
              <button onClick={() => scrape(true)} disabled={!!busy} className={GHOST} data-testid="brand-account-force-scrape-btn"><RefreshCw size={13} /> Force refresh (ignore 24h cache)</button>
            </div>
            <div className="space-y-1" data-testid="brand-account-discovered-hashtags">{d.competitors.map((c) => <div key={c.instagram_handle} className="text-xs text-[#6B6B6B]">@{c.instagram_handle} · last used {c.hashtag_used ? `#${c.hashtag_used}` : 'tagged posts only'} · discovered {c.discovered_hashtags?.length ? c.discovered_hashtags.map((t) => `#${t}`).join(' ') : '—'}</div>)}</div>
            <section><div className={LABEL}>Competitors</div><CompetitorRows rows={rows} setRows={setRows} testid="brand-account-edit-comp" />
              <div className="flex justify-end mt-2"><button onClick={saveComps} disabled={!!busy} className={PRIMARY} data-testid="brand-account-save-comps">{busy === 'save' ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save competitors</button></div>
            </section>
            <section>
              <div className={LABEL}>Brand users ({d.users.length})</div>
              <div className="space-y-1 mb-3" data-testid="brand-account-users">{d.users.map((u) => <div key={u.id} className="text-sm text-[#0F0F0F] dark:text-white">{u.name} · <span className="text-[#6B6B6B]">{u.email}</span></div>)}{!d.users.length && <p className="text-xs text-[#6B6B6B]">No users yet.</p>}</div>
              <UserForm accountId={id} onCreated={() => { load(); onChanged(); }} />
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default function AdminBrandAccounts() {
  const [accounts, setAccounts] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => { adminApi.get('/accounts').then((d) => setAccounts(d.accounts)).catch(() => setAccounts([])); }, []);
  useEffect(load, [load]);
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D] min-w-0" data-testid="admin-brand-accounts-page">
        <div className="max-w-[1200px] mx-auto space-y-6">
          <div className="flex items-end justify-between">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3"><Building2 className="text-[#FF451A]" size={30} /> Brand Accounts</h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Competitor tracking accounts for brand clients.</p>
            </div>
            <button onClick={() => setCreateOpen(true)} className={PRIMARY} data-testid="brand-account-new-btn"><Plus size={13} /> New brand account</button>
          </div>
          {accounts === null ? <Loader2 className="animate-spin" size={16} /> : (
            <div className="border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden">
              <table className="w-full" data-testid="brand-accounts-table">
                <thead><tr className="bg-black/[0.02] dark:bg-white/[0.02]">{['Brand', 'Competitors', 'Users', 'Posts', 'Last updated'].map((h) => <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400">{h}</th>)}</tr></thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.id} onClick={() => setOpenId(a.id)} className="border-t border-black/[0.06] dark:border-white/5 cursor-pointer hover:bg-[#FF451A]/[0.03]" data-testid={`brand-account-row-${a.id}`}>
                      <td className="px-4 py-3 text-sm font-semibold text-[#0F0F0F] dark:text-white">{a.brand_name}</td>
                      <td className="px-4 py-3 text-xs text-[#4B5563] dark:text-zinc-300">{a.competitors.map((c) => c.name).join(', ') || '—'}</td>
                      <td className="px-4 py-3 text-sm font-mono">{a.user_count}</td>
                      <td className="px-4 py-3 text-sm font-mono">{a.post_count}</td>
                      <td className="px-4 py-3 text-xs text-[#6B6B6B]">{dateTime(a.last_scraped_at)}</td>
                    </tr>
                  ))}
                  {!accounts.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#6B6B6B]">No brand accounts yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(a) => { setCreateOpen(false); load(); setOpenId(a.id); }} />
      <DetailSheet id={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
